import type { ChatHistoryGroup } from './components/SoteriaSidebar';
import type { ChatSummary } from './hooks/useSoteriaChat';

/**
 * Bucket chat summaries into the sections both Soteria surfaces show.
 *
 * "Today" = same calendar day, "Previous 7 Days" = within 7 days, then
 * "Previous 30 Days", then "Older". Pinned float to the top, archived drop to
 * the bottom (out of the date groups). Empty sections drop out.
 *
 * Shared by the full page's sidebar and the drawer's history view so the two
 * can't drift into grouping the same chats differently.
 */
export function groupChats(chats: Array<ChatSummary>): Array<ChatHistoryGroup> {
  const now = Date.now();
  const DAY = 24 * 60 * 60 * 1000;
  const pinned = chats.filter((c) => c.pinned && !c.archived);
  const archived = chats.filter((c) => c.archived);
  const rest = chats.filter((c) => !c.pinned && !c.archived);

  const today: Array<ChatSummary> = [];
  const week: Array<ChatSummary> = [];
  const month: Array<ChatSummary> = [];
  const older: Array<ChatSummary> = [];
  for (const c of rest) {
    const age = now - c.updatedAt.getTime();
    const sameDay =
      new Date(c.updatedAt).toDateString() === new Date().toDateString();
    if (sameDay) today.push(c);
    else if (age < 7 * DAY) week.push(c);
    else if (age < 30 * DAY) month.push(c);
    else older.push(c);
  }
  return [
    { section: 'Pinned', chats: pinned },
    { section: 'Today', chats: today },
    { section: 'Previous 7 Days', chats: week },
    { section: 'Previous 30 Days', chats: month },
    { section: 'Older', chats: older },
    { section: 'Archived', chats: archived },
  ].filter((g) => g.chats.length > 0);
}

/**
 * The most recently touched chats, newest first, for the drawer's empty state.
 * Archived chats are left out — they're deliberately put away.
 */
export function recentChats(
  chats: Array<ChatSummary>,
  limit = 5,
): Array<ChatSummary> {
  return chats
    .filter((c) => !c.archived)
    .slice()
    .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
    .slice(0, limit);
}
